import * as React from "react";
import clsx from "clsx";
import { getUnixTime, formatDistanceStrict, addDays, addHours, addMinutes, addSeconds } from "date-fns";
import { toZonedTime } from "date-fns-tz";
import { useTheme } from "../helpers/theme-provider/use-theme.mjs";
import { CountdownItem } from "./countdown-item.mjs";
const calculateTimeComponent = (x, v) => {
  return Math.floor(x / v);
};
const getTime = (timestamp) => {
  const days = calculateTimeComponent(timestamp, 24 * 60 * 60);
  const hours = calculateTimeComponent(timestamp, 60 * 60) % 24;
  const minutes = calculateTimeComponent(timestamp, 60) % 60;
  const seconds = timestamp % 60;
  return { days, hours, minutes, seconds };
};
const TIMEZONE = "Europe/Tallinn";
const Countdown = (props) => {
  const {
    days: daysProp = true,
    hours: hoursProp = true,
    minutes: minutesProp = true,
    seconds: secondsProp = true,
    announce = true,
    time,
    locale: localeProp,
    className,
    numberColor,
    theme,
    backgroundColor,
    ...rest
  } = props;
  const { locale: themeLocale } = useTheme();
  const [screenReaderText, setScreenReaderText] = React.useState("");
  const [countdownTime, setCountdownTime] = React.useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0
  });
  const locale = localeProp != null ? localeProp : themeLocale;
  const timezonedDate = React.useMemo(() => toZonedTime(/* @__PURE__ */ new Date(), TIMEZONE), []);
  const units = React.useMemo(() => {
    const unitsArray = [];
    if (daysProp) {
      unitsArray.push("days");
    }
    if (hoursProp) {
      unitsArray.push("hours");
    }
    if (minutesProp) {
      unitsArray.push("minutes");
    }
    if (secondsProp) {
      unitsArray.push("seconds");
    }
    return unitsArray;
  }, [daysProp, hoursProp, minutesProp, secondsProp]);
  const getDistance = React.useCallback(
    ({ days, hours, minutes, seconds }) => {
      if (getUnixTime(new Date(timezonedDate)) > getUnixTime(toZonedTime(new Date(time), TIMEZONE))) {
        const day2 = daysProp ? formatDistanceStrict(0, 0, {
          unit: "day",
          locale
        }) : "";
        const hour2 = hoursProp ? formatDistanceStrict(0, 0, {
          unit: "hour",
          locale
        }) : "";
        const minute2 = minutesProp ? formatDistanceStrict(0, 0, {
          unit: "minute",
          locale
        }) : "";
        const second2 = secondsProp ? formatDistanceStrict(0, 0, {
          unit: "second",
          locale
        }) : "";
        return { day: day2, hour: hour2, minute: minute2, second: second2 };
      }
      const day = daysProp ? formatDistanceStrict(addDays(timezonedDate, days), timezonedDate, { unit: "day", locale }) : "";
      const hour = hoursProp ? formatDistanceStrict(addHours(timezonedDate, hours), timezonedDate, { unit: "hour", locale }) : "";
      const minute = minutesProp ? formatDistanceStrict(addMinutes(timezonedDate, minutes), timezonedDate, { unit: "minute", locale }) : "";
      const second = secondsProp ? formatDistanceStrict(addSeconds(timezonedDate, seconds), timezonedDate, { unit: "second", locale }) : "";
      return { day, hour, minute, second };
    },
    [daysProp, hoursProp, minutesProp, secondsProp, time, timezonedDate, locale]
  );
  const timesAndUnitsArray = Object.values(getDistance(countdownTime));
  React.useEffect(() => {
    const updateScreenReaderText = (time2) => {
      setScreenReaderText(Object.values(getDistance(time2)).join(" "));
    };
    const now = getUnixTime(/* @__PURE__ */ new Date());
    let timestamp = getUnixTime(new Date(time)) - now;
    setCountdownTime(getTime(timestamp));
    updateScreenReaderText(getTime(timestamp));
    const timerId = window.setInterval(() => {
      timestamp--;
      const nextTime = getTime(timestamp);
      setCountdownTime(nextTime);
      if (!announce || nextTime.seconds === 0) {
        updateScreenReaderText(nextTime);
      }
    }, 1e3);
    return () => {
      clearInterval(timerId);
    };
  }, [announce, time, getDistance]);
  const renderTime = (unit) => {
    let timeValue;
    switch (unit) {
      case "days":
        timeValue = timesAndUnitsArray[0].split(" ")[0];
        break;
      case "hours":
        timeValue = timesAndUnitsArray[1].split(" ")[0];
        break;
      case "minutes":
        timeValue = timesAndUnitsArray[2].split(" ")[0];
        break;
      case "seconds":
        timeValue = timesAndUnitsArray[3].split(" ")[0];
        break;
    }
    if (timeValue === "üks") {
      timeValue = "1";
    }
    return parseInt(timeValue);
  };
  const renderUnit = (unit) => {
    switch (unit) {
      case "days":
        return timesAndUnitsArray[0].split(" ")[1];
      case "hours":
        return timesAndUnitsArray[1].split(" ")[1];
      case "minutes":
        return timesAndUnitsArray[2].split(" ")[1];
      case "seconds":
        return timesAndUnitsArray[3].split(" ")[1];
    }
  };
  return /* @__PURE__ */ React.createElement("div", { ...rest, className: clsx("countdown", className), "data-theme": theme }, /* @__PURE__ */ React.createElement("span", { className: "sr-only", role: announce ? "alert" : void 0, "aria-live": announce ? "polite" : void 0 }, screenReaderText), units.map((unit) => {
    return /* @__PURE__ */ React.createElement(CountdownItem, { unit: renderUnit(unit), key: unit, numberColor, backgroundColor }, renderTime(unit));
  }));
};
export {
  Countdown
};
